import Skeleton from "@ingka/skeleton";
import type { CountrySummary as CountrySummaryData } from "../api";
import styles from "./CountrySummary.module.css";

interface CountrySummaryProps {
    data: CountrySummaryData | null;
    loading: boolean;
    anchor: string;
    showInterior: boolean;
    expectedRows?: number;
}

function formatCurrency(value: number | null, currency: string): string {
    if (value === null) return "N/A";
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
}

// EUR columns are already labelled "(EUR)", so render plain numbers without the
// € symbol.
function formatNumber(value: number | null): string {
    if (value === null) return "N/A";
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(value);
}

export function CountrySummary({ data, loading, anchor, showInterior, expectedRows = 1 }: CountrySummaryProps) {
    if (loading) {
        return (
            <div className={styles.container}>
                <h3 className={styles.title}>Selection Summary</h3>
                <p className={styles.subtitle}>Total sales revenue by item category</p>
                <div className={styles.tableWrapper}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Retail Unit</th>
                                <th>Currency</th>
                                <th className={styles.valueHeader}>Frames & Doors (local)</th>
                                <th className={styles.valueHeader}>
                                    {showInterior ? "Frames & Doors & Interior (local)" : "Frames & Doors & Others (local)"}
                                </th>
                                <th className={styles.valueHeader}>Frames & Doors (EUR)</th>
                                <th className={styles.valueHeader}>
                                    {showInterior ? "Frames & Doors & Interior (EUR)" : "Frames & Doors & Others (EUR)"}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {Array.from({ length: expectedRows }, (_, i) => (
                                <tr key={i}>
                                    <td><Skeleton width="64px" height="14px" /></td>
                                    <td><Skeleton width="32px" height="14px" /></td>
                                    <td className={styles.valueCell}><Skeleton width="72px" height="14px" /></td>
                                    <td className={styles.valueCell}><Skeleton width="72px" height="14px" /></td>
                                    <td className={styles.valueCell}><Skeleton width="72px" height="14px" /></td>
                                    <td className={styles.valueCell}><Skeleton width="72px" height="14px" /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    if (!data || data.rows.length === 0) {
        return null;
    }

    // The EUR conversion only adds information when more than one currency is in
    // scope; with a single currency the local column already says everything, so
    // hide the EUR columns (and drop the now-redundant "(local)" qualifier).
    const singleCurrency = new Set(data.rows.map((row) => row.currency)).size === 1;
    const localSuffix = singleCurrency ? "" : " (local)";

    return (
        <div className={styles.container}>
            <h3 className={styles.title} title="Breakdown of sales revenue per retail unit for the current filter selection">Selection Summary</h3>
            <p className={styles.subtitle}>
                Total sales revenue by item category
            </p>
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Retail Unit</th>
                            <th>Currency</th>
                            <th className={styles.valueHeader} title={`Revenue from ${anchor} frames and doors in country's local currency`}>Frames &amp; Doors{localSuffix}</th>
                            {showInterior ? (
                                <th className={styles.valueHeader} title={`Revenue from ${anchor} frames, doors, and interior organisers in local currency`}>Frames &amp; Doors &amp; Interior{localSuffix}</th>
                            ) : (
                                <th className={styles.valueHeader} title={`Revenue from ${anchor} frames, doors, and all other companions (legs, top panels, lighting, …) in local currency`}>Frames &amp; Doors &amp; Others{localSuffix}</th>
                            )}
                            {!singleCurrency && (
                                <>
                                    <th className={styles.valueHeader} title={`Revenue from ${anchor} frames and doors converted to EUR`}>Frames &amp; Doors (EUR)</th>
                                    {showInterior ? (
                                        <th className={styles.valueHeader} title={`Revenue from ${anchor} frames, doors, and interior organisers converted to EUR`}>Frames &amp; Doors &amp; Interior (EUR)</th>
                                    ) : (
                                        <th className={styles.valueHeader} title={`Revenue from ${anchor} frames, doors, and all other companions converted to EUR`}>Frames &amp; Doors &amp; Others (EUR)</th>
                                    )}
                                </>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {data.rows.map((row) => (
                            <tr key={row.country}>
                                <td className={styles.countryCell}>{row.country}</td>
                                <td>{row.currency}</td>
                                <td className={styles.valueCell}>
                                    {formatCurrency(row.frames_doors_value_local, row.currency)}
                                </td>
                                {showInterior ? (
                                    <td className={styles.valueCell}>
                                        {formatCurrency(row.frames_doors_interior_value_local, row.currency)}
                                    </td>
                                ) : (
                                    <td className={styles.valueCell}>
                                        {formatCurrency(row.frames_doors_others_value_local, row.currency)}
                                    </td>
                                )}
                                {!singleCurrency && (
                                    <>
                                        <td className={styles.valueCell}>
                                            {formatNumber(row.frames_doors_value_eur)}
                                        </td>
                                        {showInterior ? (
                                            <td className={styles.valueCell}>
                                                {formatNumber(row.frames_doors_interior_value_eur)}
                                            </td>
                                        ) : (
                                            <td className={styles.valueCell}>
                                                {formatNumber(row.frames_doors_others_value_eur)}
                                            </td>
                                        )}
                                    </>
                                )}
                            </tr>
                        ))}
                    </tbody>
                    {data.rows.length > 1 && (
                        <tfoot>
                            <tr className={styles.totalRow}>
                                <td colSpan={2} className={styles.totalLabel}>
                                    Total
                                </td>
                                <td className={styles.totalValue}>
                                    {data.total_currency
                                        ? formatCurrency(data.total_frames_doors_value_local, data.total_currency)
                                        : "—"}
                                </td>
                                {showInterior ? (
                                    <td className={styles.totalValue}>
                                        {data.total_currency
                                            ? formatCurrency(data.total_frames_doors_interior_value_local, data.total_currency)
                                            : "—"}
                                    </td>
                                ) : (
                                    <td className={styles.totalValue}>
                                        {data.total_currency
                                            ? formatCurrency(data.total_frames_doors_others_value_local, data.total_currency)
                                            : "—"}
                                    </td>
                                )}
                                {!singleCurrency && (
                                    <>
                                        <td className={styles.totalValue}>
                                            {formatNumber(data.total_frames_doors_value_eur)}
                                        </td>
                                        {showInterior ? (
                                            <td className={styles.totalValue}>
                                                {formatNumber(data.total_frames_doors_interior_value_eur)}
                                            </td>
                                        ) : (
                                            <td className={styles.totalValue}>
                                                {formatNumber(data.total_frames_doors_others_value_eur)}
                                            </td>
                                        )}
                                    </>
                                )}
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}
